import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from './api';
import { calculateTotalGSTBreakdown, getStateNameFromGST } from './gstUtils';
import { LOGO_BASE64 } from './logoBase64';
import { roundForCalculation, roundForDisplay } from './mathUtils';

console.log('Using updated PDF generator');

export const generatePDF = async (data, type = 'estimate', companySettings = null, options = {}) => {
  // Fetch company settings if not provided
  if (!companySettings) {
    try {
      const response = await api.get('/admin/company-settings');
      companySettings = response.data;
    } catch (error) {
      console.error('Failed to fetch company settings for PDF:', error);
      // Use default settings if fetch fails - but prefer "Not Configured" for bank details
      companySettings = {
        companyName: 'IDEAL TRENDZ INTERIORS',
        addressLine1: '# Vertex Privilege, Villa No. 32, Nizampet Road',
        addressLine2: '',
        city: 'HYDERNAGAR',
        state: 'TELANGANA',
        pincode: '500072',
        contactNumber: '9014814901',
        emailId: 'idealtrendz.int@gmail.com',
        gstNumber: '36AHZPN3524A1Z9',
        panNumber: 'AHZPN3524A',
        bankName: 'Not Configured',
        accountHolderName: 'Not Configured',
        accountNumber: 'Not Configured',
        ifscCode: 'Not Configured',
        branchName: 'Not Configured'
      };
    }
  }
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  // Company Header Section with border
  doc.setLineWidth(0.5);
  doc.rect(10, 10, pageWidth - 20, 35);

  // Company Logo - Ideal Trendz
  // Use uploaded image
  try {
    doc.addImage(LOGO_BASE64, 'PNG', 15, 15, 25, 25);
  } catch (error) {
    console.warn('Failed to add logo image, falling back to text:', error);
    doc.setFillColor(0, 50, 160);
    doc.rect(15, 15, 20, 20, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('IT', 25, 28, { align: 'center' });
  }

  // Company Name and Details
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(companySettings.companyName || 'IDEAL TRENDZ INTERIORS', 46.5, 20);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  const companyAddressLine1 = companySettings.addressLine1 || '# Vertex Privilege, Villa No. 32, Nizampet Road';
  const companyAddressLine2 = [
    companySettings.addressLine2,
    companySettings.city,
    companySettings.state,
    companySettings.pincode ? companySettings.pincode : ''
  ].filter(Boolean).join(', ');

  doc.text(companyAddressLine1, 46.5, 25);
  if (companyAddressLine2) {
    doc.text(companyAddressLine2, 46.5, 28);
    doc.text(`Email: ${companySettings.emailId || 'idealtrendz.int@gmail.com'}`, 46.5, 32);
    doc.text(`Contact No. ${companySettings.contactNumber || '9014814901'}`, 46.5, 36);
  } else {
    doc.text(`Email: ${companySettings.emailId || 'idealtrendz.int@gmail.com'}`, 46.5, 29);
    doc.text(`Contact No. ${companySettings.contactNumber || '9014814901'}`, 46.5, 33);
  }

  // GST and PAN on the right
  doc.setFontSize(8);
  doc.text(`GST: ${companySettings.gstNumber || 'N/A'}`, pageWidth - 15, 25, { align: 'right' });
  doc.text(`PAN: ${companySettings.panNumber || 'N/A'}`, pageWidth - 15, 29, { align: 'right' });

  // Document Title Section
  let yPos = 45;
  doc.rect(10, yPos, pageWidth - 20, 12);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  const isProforma = data.is_proforma || data.isProforma || type === 'proforma';
  const title = type === 'invoice' ? 'TAX INVOICE' :
    type === 'closed_order' ? 'CLOSED ORDER' :
    isProforma ? 'PROFORMA INVOICE' : 'ESTIMATE';
  doc.text(title, pageWidth / 2, yPos + 8, { align: 'center' });

  // Move to next line after title
  yPos += 12;

  // Add Cancelled Status Section if applicable
  const isCancelled = data.cancellation_status === 'cancelled' || data.status === 'cancelled';
  if (isCancelled) {
    // Create a box for cancelled status
    doc.rect(10, yPos, pageWidth - 20, 16);

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0); // Black color
    doc.text('STATUS: CANCELLED', pageWidth / 2, yPos + 6, { align: 'center' });

    // Add refund status if available
    if (data.cancellation_payment_status) {
      doc.setFontSize(10);
      const refundStatusText = data.cancellation_payment_status === 'refund_initiated' ? 'Refund Initiated' :
        data.cancellation_payment_status === 'refund_completed' ? 'Refund Completed Successfully' :
          data.cancellation_payment_status === 'no_payment' ? 'No Payment' :
            data.cancellation_payment_status;
      doc.text(`REFUND STATUS: ${refundStatusText.toUpperCase()}`, pageWidth / 2, yPos + 12, { align: 'center' });
    }

    yPos += 16; // Move past the cancelled status section
  }

  // DYNAMIC HEIGHT CONFIGURATION WITH PROPER ALIGNMENT
  const LEFT_COLUMN_WIDTH = 100;
  const LEFT_MARGIN = 15;
  const LINE_HEIGHT = 4;  // Reduced from previous spacing

  // Get customer data
  const customerPhone = data.customer_phone || data.customer?.phone || '';
  const customerEmail = data.customer_email || data.customer?.email || '';
  const customerName = data.customer?.name || data.customer_name || 'WALK-IN CUSTOMER';
  const customerAddress = (data.customer_address || data.customer_city || '').replace(/\s+/g, ' ').trim();

  // Pre-calculate wrapped text for height calculation
  const wrappedName = doc.splitTextToSize(customerName.toUpperCase(), LEFT_COLUMN_WIDTH);
  const nameLines = wrappedName;  // No line limit
  const wrappedAddress = doc.splitTextToSize(customerAddress, LEFT_COLUMN_WIDTH);
  const addressLines = wrappedAddress;  // No line limit

  // Pre-calculate wrapped text for Dispatch/Delivery (targeting 8pt font specifically)
  doc.setFontSize(8);
  const dispatchText = data.dispatched_from ? `Dispatched From: ${data.dispatched_from}` : '';
  const wrappedDispatch = data.dispatched_from ? doc.splitTextToSize(dispatchText, LEFT_COLUMN_WIDTH) : [];

  const deliveredVal = data.delivered_to || data.deliveredTo;
  const deliveredText = deliveredVal ? `Delivered To: ${deliveredVal}` : '';
  const wrappedDelivered = deliveredVal ? doc.splitTextToSize(deliveredText, LEFT_COLUMN_WIDTH) : [];
  doc.setFontSize(9); // Restore to 9pt for regular party section text

  // Calculate dynamic height based on actual content
  let contentHeight = 5;  // Starting position (aligned with right side)
  contentHeight += (nameLines.length * LINE_HEIGHT);  // Name height
  if (customerAddress) contentHeight += (addressLines.length * LINE_HEIGHT);  // Address height (only if exists)
  if (customerPhone) contentHeight += LINE_HEIGHT;  // Phone
  if (customerEmail) contentHeight += LINE_HEIGHT;  // Email
  if (data.dispatched_from) contentHeight += (wrappedDispatch.length * LINE_HEIGHT); // Dispatched From (DYNAMIC)
  if (deliveredVal) contentHeight += (wrappedDelivered.length * LINE_HEIGHT); // Delivered To (DYNAMIC)
  contentHeight += 6;  // Bottom padding

  // Use the maximum of left content height and right side height (approximately 25 for right side)
  const PARTY_SECTION_HEIGHT = Math.max(contentHeight, 25);

  // Customer and Document Info Section - DYNAMIC HEIGHT
  doc.rect(10, yPos, pageWidth - 20, PARTY_SECTION_HEIGHT);

  // Left side - Customer Info with TEXT WRAPPING (ALIGNED WITH RIGHT)
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  let leftYPos = yPos + 5;  // Start at same position as right side
  doc.text('To', LEFT_MARGIN, leftYPos);
  leftYPos += LINE_HEIGHT;

  // Customer name - wrapped, BOLD
  doc.setFont('helvetica', 'bold');
  nameLines.forEach((line, index) => {
    doc.text(line, LEFT_MARGIN, leftYPos + (index * LINE_HEIGHT));
  });
  leftYPos += (nameLines.length * LINE_HEIGHT);

  // Customer address - wrapped, FULL ADDRESS
  doc.setFont('helvetica', 'normal');
  if (customerAddress) {
    addressLines.forEach((line, index) => {
      doc.text(line, LEFT_MARGIN, leftYPos + (index * LINE_HEIGHT));
    });
    leftYPos += (addressLines.length * LINE_HEIGHT);
  }

  // Phone (if available) - no extra spacing before
  if (customerPhone) {
    const phoneText = `Phone: ${customerPhone}`;
    const wrappedPhone = doc.splitTextToSize(phoneText, LEFT_COLUMN_WIDTH);
    doc.text(wrappedPhone[0], LEFT_MARGIN, leftYPos);
    leftYPos += LINE_HEIGHT;  // Add spacing after for next element
  }

  // Email (if available) - no extra spacing before
  if (customerEmail) {
    const emailText = `Email: ${customerEmail}`;
    const wrappedEmail = doc.splitTextToSize(emailText, LEFT_COLUMN_WIDTH);
    doc.text(wrappedEmail[0], LEFT_MARGIN, leftYPos);
    leftYPos += LINE_HEIGHT;
  }

  // Dispatched From (if available) - REDUCED FONT
  if (data.dispatched_from) {
    doc.setFontSize(8);
    wrappedDispatch.forEach((line, index) => {
      doc.text(line, LEFT_MARGIN, leftYPos + (index * LINE_HEIGHT));
    });
    leftYPos += (wrappedDispatch.length * LINE_HEIGHT);
    doc.setFontSize(9); // Restore for Delivered To (so it can set its own)
  }

  // Delivered To (if available) - REDUCED FONT
  if (deliveredVal) {
    doc.setFontSize(8);
    wrappedDelivered.forEach((line, index) => {
      doc.text(line, LEFT_MARGIN, leftYPos + (index * LINE_HEIGHT));
    });
    leftYPos += (wrappedDelivered.length * LINE_HEIGHT);
    doc.setFontSize(9); // Standard restore
  }

  // Right side - Document Info with FIXED ALIGNMENT
  doc.setFontSize(8);
  const docDate = new Date(data.date || new Date()).toLocaleDateString('en-GB');
  const docNumber = data.closed_order_number || data.order_number || data.estimate_number || data.invoice_number || data._id?.slice(-8) || '001';

  // Get party details from customer data
  const partyPan = data.customer?.panNumber || data.party_pan_no || '';
  const partyGst = data.customer_gst || data.customer?.gstNumber || data.party_gst_no || '';
  const partyState = data.customer_state || data.customer?.state || data.party_state_name || (partyGst ? getStateNameFromGST(partyGst) : '') || '';

  // Fixed column positions for consistent alignment
  const labelColX = pageWidth - 75;
  const valueColX = pageWidth - 12;
  let rightYPos = yPos + 5;  // Aligned with left "To" label

  // DATE
  doc.setFont('helvetica', 'bold');
  doc.text('DATE:', labelColX, rightYPos);
  doc.setFont('helvetica', 'normal');
  doc.text(docDate, valueColX, rightYPos, { align: 'right' });
  rightYPos += 4;

  // INVOICE/ORDER/ESTIMATE NUMBER
  doc.setFont('helvetica', 'bold');
  if (type === 'invoice') {
    doc.text('INVOICE No.:', labelColX, rightYPos);
  } else if (type === 'closed_order') {
    doc.text('ORDER No.:', labelColX, rightYPos);
  } else if (type === 'estimate') {
    doc.text('PROFORMA No.:', labelColX, rightYPos);
  }
  doc.setFont('helvetica', 'normal');
  doc.text(docNumber, valueColX, rightYPos, { align: 'right' });
  rightYPos += 4;

  // PARTY PAN NO
  doc.setFont('helvetica', 'bold');
  doc.text('PARTY PAN No.:', labelColX, rightYPos);
  doc.setFont('helvetica', 'normal');
  doc.text(partyPan || '', valueColX, rightYPos, { align: 'right' });
  rightYPos += 5;

  // PARTY GST NO
  doc.setFont('helvetica', 'bold');
  doc.text('PARTY GST No.:', labelColX, rightYPos);
  doc.setFont('helvetica', 'normal');
  doc.text(partyGst || '', valueColX, rightYPos, { align: 'right' });
  rightYPos += 5;

  // PARTY STATE NAME
  doc.setFont('helvetica', 'bold');
  doc.text('PARTY STATE NAME:', labelColX, rightYPos);
  doc.setFont('helvetica', 'normal');
  doc.text(partyState || '', valueColX, rightYPos, { align: 'right' });

  // Product Table - DYNAMIC POSITION based on content
  yPos += PARTY_SECTION_HEIGHT;
  const items = data.items || [];
  const isWoodWork = !!data.isWoodWork;

  console.log('📋 PDF Generator - Data Type:', isWoodWork ? 'WOOD WORK PROJECT' : 'RETAIL/NORMAL');

  // Calculate totals
  let subtotalAmount = 0;
  let totalGstAmount = 0;

  let tableConfig = {};

  if (isWoodWork) {
    /* ══ WOOD WORK TABLE LOGIC ══ */
    const tableData = [];
    const CAT_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P'];

    // Build ordered list of unique categories
    const categoryOrder = [];
    items.forEach(item => {
      const cat = item.categoryLabel || item.category_label || item.template_category || '';
      if (!categoryOrder.includes(cat)) categoryOrder.push(cat);
    });

    categoryOrder.forEach((catLabel, catIdx) => {
      // Add Section Header Row
      tableData.push([
        {
          content: `${CAT_LETTERS[catIdx] || (catIdx + 1)}. ${(catLabel || 'Section').toUpperCase()}`,
          colSpan: 9,
          styles: { fillColor: [189, 215, 238], fontStyle: 'bold', textColor: [0, 0, 0], halign: 'left' }
        }
      ]);

      const catEntries = items.filter(item => (item.categoryLabel || item.category_label || item.template_category || '') === catLabel);
      catEntries.forEach((item, siNo) => {
        // Wood Work Qty Calculation (Internal helper logic)
        const n = parseFloat(item.nos) || 0;
        const l = parseFloat(item.length) || 0;
        const b = parseFloat(item.breadth) || 0;
        const qty = (l === 0 || b === 0) ? n : parseFloat((n * l * b).toFixed(4));
        const rate = item.unit_price || 0;
        const amount = item.amount || (qty * rate);

        subtotalAmount = roundForCalculation(subtotalAmount + amount);
        // Wood work estimates usually have GST applied at the bottom total, so per-item GST calculation is done there if needed
        // But we still track per-item GST if it exists in data
        totalGstAmount = roundForCalculation(totalGstAmount + (item.gst_amount || 0));

        const itemGstAmt = (amount * (item.gst_rate || 0)) / 100;
        const itemTotal = amount + itemGstAmt;

        tableData.push([
          (siNo + 1).toString(),
          item.product_name || '',
          roundForDisplay(rate).toFixed(1),
          item.nos?.toString() || '0',
          item.length?.toString() || '0',
          item.breadth?.toString() || '0',
          qty.toString(),
          (item.gst_rate || 0).toString() + '%',
          roundForDisplay(itemTotal).toFixed(1)
        ]);
      });
    });

    tableConfig = {
      head: [['SI', 'TYPE', 'RATE (Rs.)', 'NOS', 'L', 'B', 'QTY', 'GST %', 'TOTAL (Rs.)']],
      body: tableData,
      columnStyles: {
        0: { cellWidth: 8, halign: 'center' },     // SI
        1: { cellWidth: 'auto', halign: 'left' },  // TYPE
        2: { cellWidth: 25, halign: 'center' },    // RATE
        3: { cellWidth: 10, halign: 'center' },    // NOS
        4: { cellWidth: 10, halign: 'center' },    // L
        5: { cellWidth: 10, halign: 'center' },    // B
        6: { cellWidth: 12, halign: 'center' },    // QTY
        7: { cellWidth: 15, halign: 'center' },    // GST %
        8: { cellWidth: 30, halign: 'right' }      // AMOUNT (Rs.)
      }
    };
  } else {
    /* ══ NORMAL RETAIL TABLE LOGIC ══ */
    const hasDiscounts = items.some(item => (item.discount || 0) > 0);
    const tableData = items.map((item, index) => {
      const quantity = item.quantity || 0;
      const unitPrice = item.unit_price || 0;
      const discount = item.discount || 0;
      const gstRate = item.gst_rate || 0;

      const baseAmount = roundForCalculation(quantity * unitPrice);
      const discountAmount = roundForCalculation(baseAmount * (discount / 100));
      const amount = item.amount || roundForCalculation(baseAmount - discountAmount);
      const gstAmount = item.gst_amount || roundForCalculation(amount * (gstRate / 100));
      const totalAmount = roundForCalculation(amount + gstAmount);

      subtotalAmount = roundForCalculation(subtotalAmount + amount);
      totalGstAmount = roundForCalculation(totalGstAmount + gstAmount);

      return hasDiscounts ? [
        (index + 1).toString(),
        item.product_name || `Product #${index + 1}`,
        item.hsn_code || '-',
        quantity.toString(),
        roundForDisplay(unitPrice).toFixed(1),
        item.unit_type || 'SFT',
        discount.toString() + "%",
        roundForDisplay(amount).toFixed(1),
        gstRate.toString(),
        roundForDisplay(gstAmount).toFixed(1),
        roundForDisplay(totalAmount).toFixed(1)
      ] : [
        (index + 1).toString(),
        item.product_name || `Product #${index + 1}`,
        item.hsn_code || '-',
        quantity.toString(),
        roundForDisplay(unitPrice).toFixed(1),
        item.unit_type || 'SFT',
        roundForDisplay(amount).toFixed(1),
        gstRate.toString(),
        roundForDisplay(gstAmount).toFixed(1),
        roundForDisplay(totalAmount).toFixed(1)
      ];
    });

    tableConfig = {
      head: hasDiscounts
        ? [['S.No', 'Product Description', 'HSN Code', 'QTY', 'Rate', 'Unit', 'Discount %', 'Amount', 'GST %', 'GST Amount', 'Total Amount']]
        : [['S.No', 'Product Description', 'HSN Code', 'QTY', 'Rate', 'Unit', 'Amount', 'GST %', 'GST Amount', 'Total Amount']],
      body: tableData,
      columnStyles: hasDiscounts ? {
        0: { cellWidth: 11, halign: 'center' },
        1: { cellWidth: 41, halign: 'left', cellPadding: 2 },
        2: { cellWidth: 14, halign: 'center' },
        3: { cellWidth: 11, halign: 'center' },
        4: { cellWidth: 14, halign: 'center' },
        5: { cellWidth: 10, halign: 'center' },
        6: { cellWidth: 18, halign: 'center' },
        7: { cellWidth: 20, halign: 'center' },
        8: { cellWidth: 11, halign: 'center' },
        9: { cellWidth: 19, halign: 'center' },
        10: { cellWidth: 20, halign: 'center' }
      } : {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 49, halign: 'left', cellPadding: 2 },
        2: { cellWidth: 16, halign: 'center' },
        3: { cellWidth: 11, halign: 'center' },
        4: { cellWidth: 16, halign: 'center' },
        5: { cellWidth: 11, halign: 'center' },
        6: { cellWidth: 22, halign: 'center' },
        7: { cellWidth: 12, halign: 'center' },
        8: { cellWidth: 21, halign: 'center' },
        9: { cellWidth: 22, halign: 'center' }
      }
    };
  }

  autoTable(doc, {
    startY: yPos,
    head: tableConfig.head,
    body: tableConfig.body,
    theme: 'plain',
    margin: { left: 10, right: 10, bottom: 15 },
    headStyles: {
      fillColor: [70, 130, 180],
      textColor: [255, 255, 255],
      fontSize: 9,
      fontStyle: 'bold',
      halign: 'center',
      valign: 'middle',
      cellPadding: { top: 4, bottom: 4, left: 2, right: 2 },
      lineWidth: 0.5,
      lineColor: [0, 0, 0],
      overflow: 'linebreak',
      minCellHeight: 12
    },
    bodyStyles: {
      fontSize: 8,
      textColor: [0, 0, 0],
      valign: 'middle',
      cellPadding: { top: 3, bottom: 3, left: 2, right: 2 },
      overflow: 'linebreak',
      cellWidth: 'wrap',
      lineWidth: 0.5,
      lineColor: [0, 0, 0],
      minCellHeight: 10
    },
    columnStyles: tableConfig.columnStyles,
    willDrawCell: function (data) {
      data.cell.styles.lineWidth = 0.5;
      data.cell.styles.lineColor = [0, 0, 0];
    },
    didDrawCell: function (data) {
      if (data.cell && typeof data.cell.x === 'number' && typeof data.cell.y === 'number' &&
        typeof data.cell.width === 'number' && typeof data.cell.height === 'number') {
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.5);
        const x = data.cell.x;
        const y = data.cell.y;
        const width = data.cell.width;
        const height = data.cell.height;
        doc.line(x, y, x + width, y);
        doc.line(x, y + height, x + width, y + height);
        doc.line(x, y, x, y + height);
        doc.line(x + width, y, x + width, y + height);
      }
    },
    didDrawPage: function (data) {
      try {
        if (data && data.table && data.table.head && data.table.body &&
          data.table.head.length > 0 && data.table.body.length > 0) {
          const tableX = 10;
          const tableY = data.table.head[0].y;
          const tableWidth = pageWidth - 20;
          const lastBodyRow = data.table.body[data.table.body.length - 1];
          const firstBodyRow = data.table.body[0];
          if (lastBodyRow && firstBodyRow && typeof tableY === 'number') {
            const tableHeight = lastBodyRow.y - tableY + firstBodyRow.height;
            if (tableWidth > 0 && tableHeight > 0) {
              doc.setLineWidth(0.5);
              doc.setDrawColor(0, 0, 0);
              doc.rect(tableX, tableY, tableWidth, tableHeight);
            }
          }
        }
      } catch (error) {
        console.warn('Error in didDrawPage:', error);
      }
    }
  });

  // Summary Section
  yPos = doc.lastAutoTable?.finalY || yPos + 50;
  const grandTotal = subtotalAmount + totalGstAmount;

  // Handle multiple field name formats for payment data
  const amountReceived = data.amountReceived || data.amount_received || data.paid_amount || 0;
  const totalAmount = data.totalAmount || data.total_amount || grandTotal;
  const outstanding = Math.max(0, totalAmount - amountReceived);

  console.log('📋 PDF Payment Data:', {
    amountReceived,
    totalAmount,
    outstanding,
    grandTotal,
    type
  });

  // Calculate required height for the footer
  let requiredFooterHeight = 65; // Base box height
  if (data.materialDescription) {
    const splitMaterial = doc.splitTextToSize(data.materialDescription, pageWidth - 30);
    requiredFooterHeight += (splitMaterial.length * 4) + 10;
  }
  if (data.termsAndConditions) {
    const splitTerms = doc.splitTextToSize(data.termsAndConditions, pageWidth - 30);
    requiredFooterHeight += (splitTerms.length * 4) + 10;
  }
  requiredFooterHeight += 25; // Signature space

  if (yPos + requiredFooterHeight > pageHeight - 15) {
    doc.addPage();
    yPos = 15;
  } else {
    yPos += 5; // padding after table
  }

  // Bottom section with bank details and totals
  doc.rect(10, yPos, pageWidth - 20, 60);

  // Left side - Bank Details
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('OUR BANK DETAILS', 15, yPos + 8);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(`Bank Name: ${companySettings.bankName || companySettings.bank_name || 'Not Configured'}`, 15, yPos + 15);
  doc.text(`A/C No: ${companySettings.accountNumber || companySettings.account_number || 'Not Configured'}`, 15, yPos + 20);
  doc.text(`IFSC: ${companySettings.ifscCode || companySettings.ifsc_code || 'Not Configured'}`, 15, yPos + 25);
  doc.text(`Branch: ${companySettings.branchName || companySettings.branch_name || 'Not Configured'}`, 15, yPos + 30);

  // Right side - Totals
  const totalsX = pageWidth - 70;
  doc.setFontSize(8);

  // Format currency values properly
  // Note: Use 'Rs.' instead of '₹' — the rupee symbol is not in jsPDF's built-in
  // Helvetica font and renders as '1' in the PDF output.
  const formatCurrency = (amount) => {
    const num = isNaN(amount) ? 0 : Number(amount);
    const formatted = num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return 'Rs. ' + formatted;
  };

  const labelX = totalsX;
  const valueX = pageWidth - 12;
  let summaryY = yPos + 8;

  // Calculate GST breakdown using new utility
  const buyerGST = data.customer_gst || data.gst_number || '';
  const gstBreakdown = calculateTotalGSTBreakdown(data.items || [], buyerGST, companySettings);

  // Subtotal
  doc.setFont('helvetica', 'normal');
  doc.text('Subtotal:', labelX, summaryY);
  doc.text(formatCurrency(gstBreakdown.totalTaxableAmount), valueX, summaryY, { align: 'right' });
  summaryY += 6;

  // GST Breakdown based on type
  const getGstLabel = (name, percentage) => {
    if (typeof percentage === 'string' && percentage.includes(',')) {
      return `${name}:`;
    }
    return `${name} (${percentage}%):`;
  };

  if (gstBreakdown.type === 'CGST_SGST') {
    // CGST
    if (gstBreakdown.cgstAmount > 0) {
      doc.text(getGstLabel('CGST', gstBreakdown.cgstPercentage), labelX, summaryY);
      doc.text(formatCurrency(gstBreakdown.cgstAmount), valueX, summaryY, { align: 'right' });
      summaryY += 6;
    }

    // SGST
    if (gstBreakdown.sgstAmount > 0) {
      doc.text(getGstLabel('SGST', gstBreakdown.sgstPercentage), labelX, summaryY);
      doc.text(formatCurrency(gstBreakdown.sgstAmount), valueX, summaryY, { align: 'right' });
      summaryY += 6;
    }
  } else {
    // IGST
    if (gstBreakdown.igstAmount > 0) {
      doc.text(getGstLabel('IGST', gstBreakdown.igstPercentage), labelX, summaryY);
      doc.text(formatCurrency(gstBreakdown.igstAmount), valueX, summaryY, { align: 'right' });
      summaryY += 6;
    }
  }

  // Total GST
  doc.text('Total GST:', labelX, summaryY);
  doc.text(formatCurrency(gstBreakdown.totalGST), valueX, summaryY, { align: 'right' });
  summaryY += 6;

  // Grand Total
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  const calculatedGrandTotal = gstBreakdown.totalTaxableAmount + gstBreakdown.totalGST;
  doc.text('Grand Total:', labelX, summaryY);
  doc.text(formatCurrency(calculatedGrandTotal), valueX, summaryY, { align: 'right' });
  summaryY += 6;

  // Amount Received and Total Outstanding removed per client requirement
  /*
  // Amount Received (for invoices and closed orders only, not estimates)
  if (type === 'invoice' || type === 'closed_order') {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('Amount Received:', labelX, summaryY);
    doc.text(formatCurrency(amountReceived), valueX, summaryY, { align: 'right' });
    summaryY += 6;
  }

  // Total Outstanding (for all document types)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  const calculatedOutstanding = calculatedGrandTotal - amountReceived;
  doc.text('Total Outstanding:', labelX, summaryY);
  doc.text(formatCurrency(calculatedOutstanding), valueX, summaryY, { align: 'right' });
  */

  console.log('Adding signature section...');

  // Terms and Conditions / Material Description Section
  let footerY = yPos + 65; // Start below the Bank Details/Totals box

  // Material Description Section (for Wood Work projects)
  if (data.materialDescription) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Material Description:', 15, footerY);
    footerY += 5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);

    const splitMaterial = doc.splitTextToSize(data.materialDescription, pageWidth - 30);
    doc.text(splitMaterial, 15, footerY);

    footerY += (splitMaterial.length * 4) + 5;
  }

  // Terms and Conditions Section
  if (data.termsAndConditions) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Terms and Conditions:', 15, footerY);
    footerY += 5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);

    // Wrap text to fit width
    const splitTerms = doc.splitTextToSize(data.termsAndConditions, pageWidth - 30);
    doc.text(splitTerms, 15, footerY);

    // Update Y position based on number of lines
    footerY += (splitTerms.length * 4) + 5;
  }

  // Signature Section - Dynamic position after Terms
  const signatureX = pageWidth - 50;
  const signatureLineY = footerY + 15; // Position line below the text
  const signatureTextY = footerY; // Text starts at current Y position

  if (isCancelled) {
    // For cancelled documents, draw signature line only (no text needed)
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.line(pageWidth - 80, signatureLineY, pageWidth - 20, signatureLineY);
  } else {
    // Add signature text
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text('For IDEAL TRENDZ INTERIORS', signatureX, signatureTextY, { align: 'center' });

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('Authorised Signatory', signatureX, signatureTextY + 8, { align: 'center' });

    // Draw signature line below the text
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.line(pageWidth - 80, signatureLineY, pageWidth - 20, signatureLineY);
  }

  console.log('Signature section added successfully!');

  // Download with customer name in filename
  const fileCustomerName = data.customer?.name || data.customer_name || 'Customer';
  const sanitizedCustomerName = fileCustomerName.replace(/[^a-zA-Z0-9]/g, '_');
  let docType = '';
  if (type === 'invoice') {
    docType = 'Invoice';
  } else if (type === 'closed_order') {
    docType = 'ClosedOrder';
  } else {
    docType = 'Proforma';
  }

  // Add cancellation status to filename if applicable
  let statusSuffix = '';
  if (isCancelled) {
    if (data.cancellation_payment_status === 'refund_completed') {
      statusSuffix = '_REFUND_COMPLETED';
    } else if (data.cancellation_payment_status === 'refund_initiated') {
      statusSuffix = '_REFUND_INITIATED';
    } else if (data.cancellation_payment_status === 'no_payment') {
      statusSuffix = '_NO_PAYMENT';
    } else {
      statusSuffix = '_CANCELLED';
    }
  }

  // Add Watermark to all pages
  const totalPages = doc.internal.getNumberOfPages();
  
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    
    // Draw page border
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(1);
    doc.rect(10, 10, pageWidth - 20, pageHeight - 20);

    // ══════════════════════════════════════════════
    // WATERMARK — Full diagonal, bottom-left → top-right
    // Text  : IDEAL TRENDZ INTERIOR
    // Color : Light Grey (RGB 180, 180, 180)
    // Opacity: 15%
    // Angle : 45° diagonal
    // Size  : 150pt bold
    // ══════════════════════════════════════════════

    doc.saveGraphicsState();
    
    // Set graphics state for watermark opacity (12%)
    if (typeof doc.GState !== 'undefined') {
      doc.setGState(new doc.GState({ opacity: 0.12 }));
    }

    // Watermark style - Light Gray #D3D3D3
    doc.setTextColor(211, 211, 211);
    doc.setFontSize(45);
    doc.setFont('helvetica', 'bold');

    const watermarkText = 'Ideal Trendz Interiors';

    // Spacing for the repeated pattern
    const horizontalStep = 100;
    const verticalStep = 80;

    // Iterate through the page to create a grid pattern
    // Start slightly outside margins to ensure full coverage
    for (let y = -20; y < pageHeight + 50; y += verticalStep) {
      for (let x = -20; x < pageWidth + 50; x += horizontalStep) {
        // Offset every other row for a staggered professional look
        const rowOffset = (Math.floor(y / verticalStep) % 2 === 0) ? 0 : horizontalStep / 2;
        
        doc.text(watermarkText, x + rowOffset, y, {
          align: 'center',
          angle: 45 // Top-left to bottom-right
        });
      }
    }

    doc.restoreGraphicsState();
    
    // Reset colors for safety
    doc.setTextColor(0, 0, 0);
  }

  const filename = `Idealtrendz_${docNumber}${statusSuffix}.pdf`;
  console.log('✅ PDF generation complete. Filename:', filename);
  
  if (options && options.returnBlob) {
    return {
      blob: doc.output('blob'),
      filename
    };
  }
  
  doc.save(filename);
  console.log('✅ PDF saved successfully:', filename);
};
